import {NgxUiLoaderComponent, NgxUiLoaderService} from 'ngx-ui-loader';

import {computed, effect, inject, Injectable, Injector, Signal, signal, WritableSignal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {StorageKeys} from '@core/storage';
import {localStorageSignal} from '@ddtmm/angular-signal-generators';
import {ICompanyManifest} from '@shared-types';
import {Loader} from './loader.types';
import GLOBAL_LOADER_ID = Loader.GLOBAL_LOADER_ID;
import DefaultConfig = Loader.DefaultConfig;
import NgxUiLoaderConfigExtended = Loader.NgxUiLoaderConfigExtended;

const LOCAL_COMPANY_MANIFEST$ = localStorageSignal<ICompanyManifest>(undefined, StorageKeys.GlobalKeys.COMPANY_MANIFEST);

/**
 * Service for managing application loading states and UI loaders.
 *
 * This service provides a centralized way to control loading indicators throughout the application.
 * It wraps the NgxUiLoaderService and adds additional functionality like signal-based reactivity,
 * translation support, and configuration management.
 *
 * @example
 * ```typescript
 * // Basic usage
 * loaderService.showLoader({ text: 'Loading...' });
 * loaderService.hideLoader();
 *
 * // Signal-based usage
 * const isLoading = signal(false);
 * loaderService.showSignal(isLoading);
 * ```
 */
@Injectable({
	providedIn: 'root',
})
export class LoaderService {
	/** NgxUiLoader service for managing loader states */
	private readonly ngxUiLoaderService: NgxUiLoaderService = inject(NgxUiLoaderService);

	/** Translation service for internationalizing loader text */
	private readonly translate: TranslateService = inject(TranslateService);

	/** Injector for effect context */
	private readonly injector: Injector = inject(Injector);

	/* -------------------------------------------------- */

	/** Reference to the global loader component instance */
	private globalLoaderRef: NgxUiLoaderComponent = null;

	/**
	 * Sets the reference to the global loader component.
	 * This reference is required for the loader service to function properly.
	 *
	 * @param globalLoaderRef - The NgxUiLoaderComponent instance
	 */
	public setGlobalLoaderRef(globalLoaderRef: NgxUiLoaderComponent): void {
		this.globalLoaderRef = globalLoaderRef;
	}

	/* -------------------------------------------------- */

	/** Internal writable signal for loader configuration */
	private loaderConfig: WritableSignal<NgxUiLoaderConfigExtended> = signal(undefined);

	/** Read-only signal exposing the current loader configuration */
	public readonly loaderConfigurations: Signal<NgxUiLoaderConfigExtended> = computed(() => {
		const config = this.loaderConfig();
		if (!config) return;
		const companyManifest = LOCAL_COMPANY_MANIFEST$();
		if (companyManifest?.logo) {
			config.logoUrl = companyManifest.logo.url;
			config.logoSize = undefined;
			config.logoHeight = companyManifest.logo.styles?.height || '50px';
			config.logoWidth = companyManifest.logo.styles?.width || 'auto';
		}
		console.log('loaderConfigurations', config);
		return {
			...config,
		};
	});

	/**
	 * Initializes the loader service with default configuration.
	 */
	constructor() {
		this.resetLoaderConfig();
	}

	/**
	 * Updates the loader configuration with new values.
	 * Automatically translates text values using the translation service.
	 *
	 * @param config - Partial configuration object to merge with current config
	 */
	private setLoaderConfig(config: Partial<NgxUiLoaderConfigExtended>): void {
		if (config?.text) {
			config.text = this.translate.instant(config.text);
		}
		this.loaderConfig.update((currentConfig: NgxUiLoaderConfigExtended) => {
			return {
				...DefaultConfig,
				...currentConfig,
				...(config || {}),
				loaderID: config.loaderID || GLOBAL_LOADER_ID,
			};
		});
	}

	/**
	 * Resets the loader configuration to default values.
	 * Combines NgxUiLoader default config with application-specific defaults.
	 */
	private resetLoaderConfig(): void {
		this.setLoaderConfig({
			...this.ngxUiLoaderService.getDefaultConfig(),
			...DefaultConfig,
		});
	}

	/**
	 * Shows the loader with the specified configuration.
	 * Only works if a global loader reference has been set.
	 *
	 * @param config - Configuration object for the loader appearance and behavior
	 * @example
	 * ```typescript
	 * loaderService.showLoader({
	 *   text: 'Please wait...',
	 *   loaderID: 'custom-loader'
	 * });
	 * ```
	 */
	public showLoader(config: Partial<Loader.NgxUiLoaderConfigExtended>): void {
		this.setLoaderConfig(config);
		this.ngxUiLoaderService.startLoader(config.loaderID || GLOBAL_LOADER_ID);
	}

	/**
	 * Hides the loader and resets configuration to defaults.
	 * Only works if a global loader reference has been set.
	 *
	 * @param loaderID - Optional loader ID to hide. Defaults to global loader ID
	 * @example
	 * ```typescript
	 * loaderService.hideLoader(); // Hide global loader
	 * loaderService.hideLoader('custom-loader'); // Hide specific loader
	 * ```
	 */
	public hideLoader(loaderID: string = GLOBAL_LOADER_ID): void {
		if (this.globalLoaderRef) {
			this.resetLoaderConfig();
			this.ngxUiLoaderService.stopLoader(loaderID);
		}
	}

	/**
	 * Creates a reactive effect that shows/hides the loader based on a signal value.
	 * This method enables declarative loader management using Angular signals.
	 *
	 * @param showSignal - Signal that controls loader visibility and configuration
	 *   - `false` or falsy: hides the loader
	 *   - `true`: shows loader with empty text
	 *   - `string`: shows loader with the string as text
	 *   - `object`: shows loader with the object as configuration
	 *
	 * @example
	 * ```typescript
	 * const isLoading = signal(false);
	 * const loadingText = signal('Processing...');
	 * const loadingConfig = signal({ text: 'Please wait', overlay: true });
	 *
	 * loaderService.showSignal(isLoading);
	 * loaderService.showSignal(loadingText);
	 * loaderService.showSignal(loadingConfig);
	 * ```
	 */
	public showSignal(showSignal: Signal<boolean | string | Partial<Loader.NgxUiLoaderConfigExtended>>): void {
		effect(
			() => {
				const show = showSignal();
				if (!show) {
					this.hideLoader();
				} else if (typeof show === 'boolean') {
					show
						? this.showLoader({
								text: '',
							})
						: this.hideLoader();
				} else if (typeof show === 'string') {
					this.showLoader({text: show});
				} else if (typeof show === 'object') {
					this.showLoader(show);
				}
			},
			{
				injector: this.injector,
			},
		);
	}
}
